import { forwardRef, type InputHTMLAttributes } from 'react';
import { controlClass } from '../lib/control.js';
import { cn } from '../lib/utils.js';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Show the invalid treatment. Sets `aria-invalid` so the styling and what
   *  assistive tech is told can never disagree. */
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, ...props }, ref) => (
    // `aria-invalid` is written after the spread on purpose — it is derived
    // from `props` already, so letting the spread land on top of it would just
    // undo `error` whenever a caller also passed `aria-invalid={false}`.
    <input
      ref={ref}
      className={cn(controlClass, className)}
      {...props}
      aria-invalid={error ? true : props['aria-invalid']}
      // Mirrors `required` the same way. Native `required` maps to the required
      // state on its own, so this is belt-and-braces on an `<input>` — but it
      // is written here anyway because it is what makes ONE rule true across
      // the whole control family: `required` on any of these four is announced,
      // including `<Combobox>`, which has no native `required` to fall back on.
      // Pair it with `<Label required>` for the visible half.
      aria-required={props.required ? true : props['aria-required']}
    />
  )
);
Input.displayName = 'Input';
